import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import ApiServices from "../services/ApiServices";
import {
  EG_ADD_TO_CART,
  EG_GET_CART_DETAILS,
  EG_REMOVE_FROM_CART,
} from "../constants/api-constants";
const EgCartContext = createContext();

const CART_STORAGE_KEY = "eg_cart_items";

const EG_CART_FALLBACK = {
  cart: [],
  cartMetadata: { rowVersion: null, cartId: null, currency: null },
  count: 0,
  setCartCount: () => {},
  addToCart: () => Promise.resolve(),
  removeFromCart: () => Promise.resolve(),
  toggleItem: () => {},
  clearCart: () => {},
  clearInvalidItems: () => {},
  getCartDetails: () => {
    console.error(" Using FALLBACK getCartDetails (returns empty promise)");
    return Promise.resolve();
  },
  updateCartItemSubscription: () => Promise.resolve(),
};

export const EgCartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error("Failed to load cart from localStorage:", error);
      return [];
    }
  });

  const [cartMetadata, setCartMetadata] = useState({
    rowVersion: null,
    cartId: null,
    currency: null,
    userId: null,
  });

  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      console.error("Failed to save cart to localStorage:", error);
    }
  }, [cart]);

  const addToCart = async (item) => {
    try {
      const defaultSub =
        item.defaultSubscription || item.subscriptionOptions?.[0];
      const isFreeContent = !defaultSub && item.price === "$0";
      let payload;
      if (isFreeContent) {
        payload = {
          itemId: item.id,
          amount: 0,
          transactionType: 2,
          frequency: 99,
          isRecurring: false,
        };
      } else if (defaultSub) {
        payload = {
          itemId: item.id,
          amount: defaultSub.value,
          transactionType: 2,
          frequency: defaultSub.type,
          isRecurring: defaultSub.isRecurring || false,
        };
      } else {
        console.error(
          "No subscription available for this item and it's not free",
        );
        return { success: false, error: "No subscription available" };
      }

      const response = await ApiServices.httpPost(EG_ADD_TO_CART, payload);

      if (response.responseStatus?.isSuccess) {
        const cartData = response.result;
        const addedItem = cartData?.items?.find(
          (cartItem) => cartItem.itemId === item.id,
        );
        const itemWithCartData = {
          ...item,
          cartItemId: addedItem?.cartItemId,
          rowVersion: addedItem?.rowVersion,
        };
        setCart((prev) => [...prev, itemWithCartData]);

        // Update cart count from API response
        if (cartData?.items) {
          setCartCount(cartData.items.length);
        }

        return { success: true };
      }
      return { success: false, error: response.responseStatus?.error };
    } catch (error) {
      console.error("Add to cart error:", error);
      return { success: false, error: error.message };
    }
  };

  const removeFromCart = async (item) => {
    try {
      // Use itemId if available, otherwise fall back to id
      const contentId = item.itemId || item.id || 0;
      const transactionType = item.transactionType || 5; // Default to 5 if not provided
      const entityType = item.entityType || 1; // Default to 1 if not provided
      const url = EG_REMOVE_FROM_CART(contentId);
      const payload = {
        transactionType,
        entityType,
      };

      const response = await ApiServices.httpDelete(url, payload);
      if (response.responseStatus?.isSuccess) {
        setCart((prev) => prev.filter((c) => (c.itemId || c.id) !== contentId));

        // Update cart count from API response
        const cartData = response.result;
        if (cartData?.items) {
          setCartCount(cartData.items.length);
        }

        return { success: true };
      }
      console.error("Remove failed:", response.responseStatus?.error);
      return { success: false, error: response.responseStatus?.error };
    } catch (error) {
      console.error("Remove from cart error:", error);
      return { success: false, error: error.message };
    }
  };

  const toggleItem = (item) => {
    setCart((prev) => {
      const exists = prev.find((c) => c.id === item.id);
      if (exists) {
        return prev.filter((c) => c.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const clearInvalidItems = () => {
    setCart((prev) => prev.filter((item) => item.cartItemId));
  };

  const getCartDetails = useCallback(async () => {
    try {
      const get_cart_details_url = EG_GET_CART_DETAILS("USD");
      const response = await ApiServices.httpGet(get_cart_details_url);
      if (response.responseStatus?.isSuccess) {
        const cartData = response.result;
        if (cartData?.items) {
          setCart(cartData.items);
          setCartCount(cartData.items.length);
          setCartMetadata({
            rowVersion: cartData.rowVersion,
            cartId: cartData.cartId,
            currency: cartData.currency,
            userId: cartData.userId,
          });
          return { success: true, data: cartData.items, metadata: cartData };
        }
        return { success: true, data: [] };
      }
      console.error("Get cart details failed:", response.responseStatus?.error);
      return { success: false, error: response.responseStatus?.error };
    } catch (error) {
      console.error("Get cart details error:", error);
      return { success: false, error: error.message };
    }
  }, []);

  const value = useMemo(
    () => ({
      cart,
      cartMetadata,
      count: cartCount,
      setCartCount,
      addToCart,
      removeFromCart,
      toggleItem,
      clearCart,
      clearInvalidItems,
      getCartDetails,
    }),
    [cart, cartMetadata, cartCount, getCartDetails],
  );

  return (
    <EgCartContext.Provider value={value}>{children}</EgCartContext.Provider>
  );
};

EgCartProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useEGCart = () => {
  const context = useContext(EgCartContext);
  if (!context) {
    return EG_CART_FALLBACK;
  }
  return context;
};
